import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { CartItem, Product, ProductVariant } from "../types";
import { trackMarketing } from "./marketing";

type CartContextValue = {
  items: CartItem[];
  itemCount: number;
  subtotal: number;
  drawerOpen: boolean;
  setDrawerOpen: (open: boolean) => void;
  add: (product: Product, variant: ProductVariant, quantity?: number) => void;
  update: (sku: string, quantity: number) => void;
  remove: (sku: string) => void;
  clear: () => void;
};

const CartContext = createContext<CartContextValue | null>(null);
const storageKey = "blessed-cart-v1";

function initialItems(): CartItem[] {
  try {
    const stored = localStorage.getItem(storageKey);
    return stored ? (JSON.parse(stored) as CartItem[]) : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(initialItems);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(items));
  }, [items]);

  const value = useMemo<CartContextValue>(() => {
    const add = (product: Product, variant: ProductVariant, quantity = 1) => {
      if (variant.price === null || !variant.shopifyVariantId) return;
      setItems((current) => {
        const found = current.find((item) => item.variantSku === variant.sku);
        if (found) {
          return current.map((item) =>
            item.variantSku === variant.sku
              ? { ...item, quantity: Math.min(99, item.quantity + quantity) }
              : item,
          );
        }
        return [
          ...current,
          {
            handle: product.handle,
            title: product.title,
            image: product.images[0],
            brand: product.brand,
            variantName: variant.name,
            variantSku: variant.sku,
            shopifyVariantId: variant.shopifyVariantId,
            price: variant.price,
            quantity,
          },
        ];
      });
      trackMarketing("AddToCart", {
        content_ids: [variant.sku],
        content_type: "product",
        contents: [{ id: variant.sku, quantity }],
        content_name: product.title,
        value: Number((variant.price * quantity).toFixed(2)),
        currency: "USD",
      });
      setDrawerOpen(true);
    };
    const update = (sku: string, quantity: number) =>
      setItems((current) =>
        quantity <= 0
          ? current.filter((item) => item.variantSku !== sku)
          : current.map((item) =>
              item.variantSku === sku ? { ...item, quantity: Math.min(99, quantity) } : item,
            ),
      );
    const remove = (sku: string) => setItems((current) => current.filter((item) => item.variantSku !== sku));
    const clear = () => setItems([]);
    return {
      items,
      itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
      subtotal: items.reduce((sum, item) => sum + (item.price ?? 0) * item.quantity, 0),
      drawerOpen,
      setDrawerOpen,
      add,
      update,
      remove,
      clear,
    };
  }, [drawerOpen, items]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const value = useContext(CartContext);
  if (!value) throw new Error("useCart must be used inside CartProvider");
  return value;
}
